import {
	CalendarOutlined,
	DollarOutlined,
	GiftOutlined,
	SearchOutlined,
} from '@ant-design/icons';
import { Modal, Space, Table, Tag, Tooltip } from 'antd';
import { timeLogsApi } from 'Api/timeLogs';
import Buttons from 'components/Global/Buttons';
import DatePickerField from 'components/Global/DatePicker';
import useDebounce from 'components/Global/Hooks/UseDebounce';
import SearchComponent from 'components/Global/SearchComponent';
import InputHoursField from 'components/InputShiftHoursField';
import dayjs from 'dayjs';
import React, { useCallback, useEffect, useState } from 'react';
import {
	minutesToTimeString,
	timeStringToMinutes,
	toastText,
} from 'utils/utils';
import styles from './index.module.scss';
import './index.scss';

const { Column } = Table;

const MonthlyTimeLogsModal = ({
	isOpen,
	handleCancel,
	fetchEmployeesTimeActivities,
	startDate,
}: any) => {
	const [employeeMonthlyHours, setEmployeeMonthlyHours] = useState<any>([]);
	const [totalRecords, setTotalRecords] = useState(0);
	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(31);
	const [isTimeLogsLoading, setIsTimeLogsLoading] = useState(false);
	const [isUpdateLoading, setIsUpdateLoading] = useState(false);
	const [searchValue, setSearchValue] = useState('');
	const [date, setDate] = useState<any>(dayjs(startDate));
	const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
	const [localChanges, setLocalChanges] = useState(new Map());
	// New state to store previous data when checkboxes are unchecked
	const [previousHoursData, setPreviousHoursData] = useState<
		Record<string, string>
	>({});

	const [isHoliday, setIsHoliday] = useState(false);
	const [holiday, setHoliday] = useState('');
	const [isSunday, setIsSunday] = useState(false);

	const tableChangeHandler = (pagination: any, filters: any, sorter: any) => {
		setTotalRecords(pagination.total);
		setCurrentPage(pagination.current);
		setPageSize(pagination.pageSize);
	};

	const handleSearch = (value: string) => {
		setSearchValue(value);
		setCurrentPage(1);
	};

	const debounceSearch = useDebounce(searchValue, 500);

	const handleSearchApi = async (value: string) => {
		//search api
	};

	const handleDate = (value: string) => {
		setDate(value);
	};

	const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
		// Store previous hours data for unchecked items
		const newPreviousData: any = { ...previousHoursData };

		// Find items that were unchecked (in selectedRowKeys but not in newSelectedRowKeys)
		const uncheckedItems = selectedRowKeys.filter(
			(key) => !newSelectedRowKeys.includes(key),
		);

		// Find items that were checked (in newSelectedRowKeys but not in selectedRowKeys)
		const checkedItems = newSelectedRowKeys.filter(
			(key) => !selectedRowKeys.includes(key),
		);

		const updatedData = employeeMonthlyHours.map((log: any) => {
			if (uncheckedItems.includes(log.employeeId)) {
				if (!newPreviousData[log.employeeId]) {
					newPreviousData[log.employeeId] = {};
				}
				if (log.hours && log.hours !== '00:00') {
					newPreviousData[log.employeeId].hours = log.hours;
				}
				if (log.timeShiftHours && log.timeShiftHours !== '00:00') {
					newPreviousData[log.employeeId].timeShiftHours = log.timeShiftHours;
				}
				return {
					...log,
					isDisable: true,
					hours: '00:00',
					timeShiftHours: '00:00',
				};
			} else if (checkedItems.includes(log.employeeId)) {
				const previous = newPreviousData[log.employeeId] || {};
				return {
					...log,
					isDisable: false,
					hours: previous.hours || log.hours || '00:00',
					timeShiftHours:
						previous.timeShiftHours || log.timeShiftHours || '00:00',
				};
			} else if (newSelectedRowKeys.includes(log.employeeId)) {
				return {
					...log,
					isDisable: false,
				};
			} else if (selectedRowKeys.includes(log.employeeId)) {
				return {
					...log,
					isDisable: true,
					hours: '00:00',
				};
			}
			return log;
		});

		// Update total hours row
		const totalRow = updatedData.find(
			(row: any) => row.employeeName === 'Total',
		);
		const rowsToSum = updatedData.filter(
			(row: any) =>
				row.employeeName !== 'Total' &&
				newSelectedRowKeys.includes(row.employeeId),
		);

		const totalHours = rowsToSum.reduce((acc: number, row: any) => {
			return acc + timeStringToMinutes(row.hours);
		}, 0);

		if (totalRow) {
			totalRow.hours = minutesToTimeString(totalHours);
			totalRow.timeShiftHours = minutesToTimeString(
				rowsToSum.reduce((acc: number, row: any) => {
					return acc + timeStringToMinutes(row.timeShiftHours);
				}, 0),
			);
		}

		// Update states
		setPreviousHoursData(newPreviousData);
		setEmployeeMonthlyHours(updatedData);
		setSelectedRowKeys(newSelectedRowKeys);
	};

	const rowSelection = {
		selectedRowKeys,
		onChange: onSelectChange,
		getCheckboxProps: (record: any) => ({
			disabled:
				record.isTotal ||
				record.leaveDetails.isLwp ||
				record.leaveDetails.isPaidLeave ||
				record.isPayrollRun,
		}),
	};

	const handleLogChange = (id: string, name: string, value: any) => {
		// Store the change in localChanges
		setLocalChanges((prev) => {
			const newChanges = new Map(prev);
			const existingChanges = newChanges.get(id) || {};
			newChanges.set(id, {
				...existingChanges,
				[name]: value,
				invalidHours: false,
				invalidTimeShiftHours: false,
				invalidOvertimeHours: false,
			});
			return newChanges;
		});

		// Rest of your existing handleLogChange logic remains the same
		let totalHours = 0;
		let totalTimeShiftHours = 0;

		const _data = employeeMonthlyHours.map((item: any, index: number) => {
			const isTarget = item.employeeId === id;
			const isTotalRow = index === employeeMonthlyHours.length - 1;

			if (isTarget) {
				const updatedItem = {
					...item,
					[name]: value,
					invalidHours: false,
					invalidTimeShiftHours: false,
					invalidOvertimeHours: false,
				};

				if (!isTotalRow) {
					if (name === 'hours') {
						totalHours += timeStringToMinutes(value);
						totalTimeShiftHours += timeStringToMinutes(
							item.timeShiftHours || '00:00',
						);
					} else if (name === 'timeShiftHours') {
						totalTimeShiftHours += timeStringToMinutes(value);
						totalHours += timeStringToMinutes(item.hours || '00:00');
					}
				}

				return updatedItem;
			}

			if (isTotalRow) {
				return { ...item };
			}

			const itemHours = timeStringToMinutes(item.hours || '00:00');
			const itemTimeShiftHours = timeStringToMinutes(
				item.timeShiftHours || '00:00',
			);

			totalHours += itemHours;
			totalTimeShiftHours += itemTimeShiftHours;

			return { ...item };
		});

		// Update the total row after the loop
		const updatedData = _data.map((item: any, index: number) => {
			if (index === _data.length - 1) {
				return {
					...item,
					hours: minutesToTimeString(totalHours),
					timeShiftHours: minutesToTimeString(totalTimeShiftHours),
				};
			}
			return item;
		});

		setEmployeeMonthlyHours(updatedData);
	};

	const mergeLocalChangesWithApiData = useCallback(
		(apiData: any[]) => {
			if (localChanges.size === 0) return apiData;

			const mergedData = apiData.map((item) => {
				if (item.isTotal) return item;

				const localChange = localChanges.get(item.employeeId);
				if (localChange) {
					return { ...item, ...localChange };
				}
				return item;
			});

			// Recalculate total row
			const totalRow = mergedData.find((row) => row.employeeName === 'Total');
			const rowsToSum = mergedData.filter(
				(row) =>
					row.employeeName !== 'Total' &&
					selectedRowKeys.includes(row.employeeId),
			);

			if (totalRow) {
				const totalHours = rowsToSum.reduce((acc, row) => {
					return acc + timeStringToMinutes(row.hours || '00:00');
				}, 0);

				const totalTimeShiftHours = rowsToSum.reduce((acc, row) => {
					return acc + timeStringToMinutes(row.timeShiftHours || '00:00');
				}, 0);

				totalRow.hours = minutesToTimeString(totalHours);
				totalRow.timeShiftHours = minutesToTimeString(totalTimeShiftHours);
			}

			return mergedData;
		},
		[localChanges, selectedRowKeys],
	);
	const fetchMonthlyTimeLogs = async () => {
		try {
			setIsTimeLogsLoading(true);
			const query = {
				date: dayjs(date).format('YYYY-MM-DD'),
				// jobGrade: selectedJobGrade,
				page: currentPage,
				pageSize: pageSize - 1,
				search: debounceSearch.trim(),
			};
			const response = await timeLogsApi.getMonthlyTimeLogs(query);

			setTotalRecords(response.data.total);

			const mergedData = mergeLocalChangesWithApiData(response.data.data?.data);
			setEmployeeMonthlyHours(mergedData);

			const defaultSelectedKeys = response.data.data.data
				.filter((item: any) => !item.isTotal)
				.map((item: any) => item.employeeId);

			setSelectedRowKeys(defaultSelectedKeys);
			setIsSunday(response.data.data?.isSunday);
			setIsHoliday(response.data.data?.isHoliday);
			setHoliday(response.data.data?.holidayName);

			// Clear previous hours data when fetching new data
			setPreviousHoursData({});
		} catch (err) {
		} finally {
			setIsTimeLogsLoading(false);
		}
	};

	const handleSubmit = async () => {
		try {
			setIsUpdateLoading(true);

			// Get visible employees (current page)
			const visibleEmployees = employeeMonthlyHours.filter(
				(item: any) => !item.isTotal,
			);

			// Merge visible employees with ALL local changes
			const allEmployeesToSubmit: any[] = visibleEmployees.map((log: any) => {
				const localChange = localChanges.get(log.employeeId);

				return {
					employeeId: log.employeeId,
					totalHours: log.isDisable
						? 0
						: timeStringToMinutes(localChange?.hours || log.hours),
					totalShiftHours: log.isDisable
						? 0
						: timeStringToMinutes(
								localChange?.timeShiftHours || log.timeShiftHours,
							),
				};
			});

			// Add ALL employees from localChanges that aren't on current page
			localChanges.forEach((changes, employeeId) => {
				const alreadyIncluded = allEmployeesToSubmit.some(
					(emp) => emp.employeeId === employeeId,
				);

				if (!alreadyIncluded) {
					allEmployeesToSubmit.push({
						employeeId: employeeId,
						totalHours: timeStringToMinutes(changes.hours || '00:00'),
						totalShiftHours: timeStringToMinutes(
							changes.timeShiftHours || '00:00',
						),
					});
				}
			});

			const data = {
				date: dayjs(date).format('YYYY-MM-DD'),
				employeeHoursData: allEmployeesToSubmit,
			};

			await timeLogsApi.createMonthlyTimeLogs(data);
			setLocalChanges(new Map()); // Clear after successful submission
			fetchEmployeesTimeActivities();

			toastText('Monthly Attendance updated successfully', 'success');
			handleCancel();
		} catch (error: any) {
			let message = 'Something went wrong in submitting Daily Attendance';
			if (error.response?.data?.message) {
				message = error.response.data.message;
			}
			toastText(message, 'error');
		} finally {
			setIsUpdateLoading(false);
			fetchMonthlyTimeLogs();
		}
	};

	const buttons = [
		{
			text: 'Cancel',
			isLoading: false,
			className: 'btn-cancel',
			isSubmit: false,
			disabled: isUpdateLoading || isTimeLogsLoading,
			onclick: () => {
				handleCancel();
			},
			fontSize: '1.8rem',
			minWidth: '12rem',
			minHeight: '4rem',
		},
		{
			text: 'Submit',
			isLoading: isUpdateLoading,
			className: 'btn-blue',
			isSubmit: false,

			disabled: isUpdateLoading || isTimeLogsLoading,
			onclick: () => {
				handleSubmit();
			},
			fontSize: '1.8rem',
			minWidth: '12rem',
			minHeight: '4rem',
		},
	];

	useEffect(() => {
		fetchMonthlyTimeLogs();
	}, [date, pageSize, currentPage, debounceSearch]);

	return (
		<Modal
			open={isOpen}
			closable={false}
			style={{ minWidth: '1000px' }}
			rootClassName="monthly-logs-root"
			onCancel={handleCancel}
			maskClosable={false}
			className="monthly-modal"
			centered
			footer={() => {
				return (
					<div className="modal">
						<div className={styles['modal-header']}>
							<Space>
								<SearchComponent
									className={styles['modal-header-item']}
									placeHolder="Search by code or name..."
									prefixIcon={<SearchOutlined />}
									handleChange={handleSearch}
									handleChangeApi={handleSearchApi}
									value={searchValue}
									size="large"
								/>
								{localChanges.size > 0 && (
									<Tag color="orange">
										{localChanges.size} unsaved change
										{localChanges.size > 1 ? 's' : ''}
									</Tag>
								)}
							</Space>
							<div className={styles['modal-header-filter']}>
								{isSunday && (
									<Tooltip title="All hours on Sunday are considered overtime">
										<Tag
											style={{
												cursor: 'pointer',
												fontSize: '14px',
												padding: '8px',
											}}
											color="#584495"
										>
											<div
												style={{
													display: 'flex',
													gap: '7px',
												}}
											>
												<span>
													<CalendarOutlined />
												</span>
												<span>Sunday</span>
											</div>
										</Tag>
									</Tooltip>
								)}
								{isHoliday && holiday && (
									<Tooltip title="All hours on Holiday are considered overtime">
										<Tag
											style={{
												cursor: 'pointer',
												fontSize: '14px',
												padding: '8px',
											}}
											color="#584495"
										>
											<div
												style={{
													display: 'flex',
													gap: '7px',
												}}
											>
												<span>
													<GiftOutlined />
												</span>
												<span>{holiday}</span>
											</div>
										</Tag>
									</Tooltip>
								)}

								<DatePickerField
									name="date"
									onChange={handleDate}
									required={false}
									value={date && dayjs(date)}
									isError={false}
									disabled={isTimeLogsLoading}
									placeholder="Select Date"
								/>
							</div>
						</div>
						<div className={styles['modal-body']}>
							<Table
								dataSource={employeeMonthlyHours}
								scroll={{ y: 'calc(100vh - 305px)' }}
								pagination={{
									total: totalRecords,
									current: currentPage,
									pageSize,
									showSizeChanger: false,
									pageSizeOptions: [10, 20, 50, 100, 200],
								}}
								onChange={tableChangeHandler}
								rowKey={(record) => record.employeeId}
								loading={isTimeLogsLoading}
								rowSelection={rowSelection}
								rowClassName={(record) => (record.isTotal ? 'sticky-row' : '')}
							>
								<Column
									title="Employee Name | Code"
									dataIndex="employeeName"
									key="employeeName"
									className="bg-white"
									width="50%"
									render={(text: string, record: any) => {
										if (record.isTotal) {
											return (
												<span className="color-purple font-bold">Total</span>
											);
										}
										return (
											<Tooltip
												title={(() => {
													if (record.leaveDetails.isHalfDay) {
														return 'On Half Day leave';
													} else if (
														record.leaveDetails.isLwp ||
														record.leaveDetails.isPaidLeave
													) {
														return 'On Full Day Leave';
													}
													return '';
												})()}
											>
												<span
													style={{
														color:
															record.leaveDetails.isLwp ||
															record.leaveDetails.isPaidLeave
																? 'red'
																: 'inherit',
														display: 'flex',
														alignItems: 'center',
														gap: '0.5rem',
													}}
												>
													{record.isPayrollRun && record?.payrollStatus && (
														<Tooltip
															title={
																record.payrollStatus === 'APPROVED' ||
																record.payrollStatus === 'FINALIZE'
																	? 'Payroll is Approved/Finalized'
																	: 'Payroll is Pending'
															}
														>
															<DollarOutlined
																style={{
																	color:
																		record.payrollStatus === 'APPROVED' ||
																		record.payrollStatus === 'FINALIZE'
																			? 'green'
																			: 'grey',
																}}
															/>
														</Tooltip>
													)}
													<span>{text}</span> |{' '}
													<span
														style={{
															color: '#584495',
														}}
													>
														{record?.employeeCode}
													</span>
												</span>
											</Tooltip>
										);
									}}
								/>
								<Column
									title="Overtime Hours"
									dataIndex="hours"
									key="hours"
									className="bg-white"
									width="30%"
									render={(text: any, data: any) => {
										return data.isTotal ? (
											(() => {
												return (
													<p
														className="color-purple font-bold"
														style={{
															width: '100px',
															textAlign: 'right',
														}}
													>
														{' '}
														{text}
													</p>
												);
											})()
										) : (
											<InputHoursField
												value={
													data.leaveDetails.isLwp ||
													data.leaveDetails.isPaidLeave
														? '00:00'
														: text
												}
												onChange={(value) => {
													handleLogChange(data.employeeId, 'hours', value);
												}}
												disabled={
													data.leaveDetails.isLwp ||
													data.leaveDetails.isPaidLeave ||
													data.isPayrollRun ||
													data.isDisable
												}
												label="Total Hours"
												required={true}
												name="hours"
												isError={data.invalidTotalHours}
												showLabel={false}
												size="middle"
												style={{
													width: '100px',
													textAlign: 'right',
												}}
											/>
										);
									}}
								/>
								<Column
									title=" Shift Difference Hours"
									dataIndex="timeShiftHours"
									key="timeShiftHours"
									className="bg-white"
									width="30%"
									render={(text: any, data: any) => {
										if (data.isTotal) {
											return (
												<p
													className="color-purple font-bold"
													style={{
														width: '100px',
														textAlign: 'right',
													}}
												>
													{' '}
													{text}
												</p>
											);
										} else {
											return (
												<InputHoursField
													value={
														data.leaveDetails.isLwp ||
														data.leaveDetails.isPaidLeave ||
														isHoliday
															? '00:00'
															: text
													}
													onChange={(value) => {
														handleLogChange(
															data.employeeId,
															'timeShiftHours',
															value,
														);
													}}
													disabled={
														data.leaveDetails.isLwp ||
														data.leaveDetails.isPaidLeave ||
														data.isPayrollRun ||
														isHoliday ||
														data.isDisable
													}
													label="Total Hours"
													required={true}
													name="timeShiftHours"
													isError={data.invalidTotalHours}
													showLabel={false}
													size="middle"
													style={{
														width: '100px',
														textAlign: 'right',
													}}
												/>
											);
										}
									}}
								/>
							</Table>
						</div>
						<div>
							<Buttons buttons={buttons} />
						</div>
					</div>
				);
			}}
		></Modal>
	);
};

export default MonthlyTimeLogsModal;
